import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { CloseButton } from '@zillow/constellation-10';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useDetailPanelSafe } from './DetailPanelContext';
import * as styles from './detailPanel.styles';

type DetailPanelHeaderProps = {
  onClose: () => void;
  actions?: ReactNode;
  closeLabel?: string;
  subtitle?: string;
};

/**
 * Detail panel header — title comes from the panel's `openPanel(...,
 * { config: { title } })` option. Legacy HALO also fell back to the
 * current listing's displayName / propertyName via Redux; here we expect
 * callers to supply the title through panelConfig.
 */
export const DetailPanelHeader = ({ onClose, actions, closeLabel, subtitle }: DetailPanelHeaderProps) => {
  const t = useTranslations('common');
  const panelContext = useDetailPanelSafe();
  const panelTitle = (panelContext?.panelConfig as { title?: string } | undefined)?.title ?? '';

  return (
    <header className={styles.panelHeader}>
      <div className={styles.panelHeaderColumns}>
        <div className={styles.panelHeaderLeft}>
          {closeLabel ? (
            <button className={styles.panelBackButton} onClick={onClose} aria-label={closeLabel}>
              <LucideIcon icon={ChevronLeft} size="sm" />
              <span>{closeLabel}</span>
            </button>
          ) : (
            <CloseButton onClick={onClose} title={t('closePanel')} />
          )}
          <div>
            <h2 className={styles.panelHeaderTitle}>{panelTitle}</h2>
            {subtitle && <p className={styles.panelHeaderSubtitle}>{subtitle}</p>}
          </div>
        </div>
        <div className={styles.panelHeaderRight}>{actions}</div>
      </div>
    </header>
  );
};
